import { supabase } from '../config/supabaseClient';
import { withErrorHandling, createAppError, ErrorCategory } from '../utils/errorHandler';
import { 
  MediaItem, 
  MediaFolder, 
  MediaCollection, 
  MediaFileType, 
  CollectionType,
  SocialPlatform
} from '../types/socialMediaPlanner';

/**
 * Service for enhanced media library management including
 * videos, carousel posts, folder organization and tagging
 */
export class MediaLibraryService {
  /**
   * Validate user authentication
   */
  private async validateAuthentication(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw createAppError(
        'Not authenticated',
        ErrorCategory.AUTH,
        401,
        'You must be logged in to use this feature'
      );
    }
    return session.user.id;
  }

  /**
   * Upload media file(s) to the media library
   * Supports single image/video or multiple files for carousels
   */
  async uploadMedia(
    files: File[],
    options: {
      folderId?: string;
      collectionName?: string;
      collectionType?: CollectionType;
      platforms?: SocialPlatform[];
      tags?: string[];
      altText?: string;
      caption?: string;
    } = {}
  ): Promise<MediaItem[] | MediaCollection> {
    return withErrorHandling(async () => {
      try {
        const userId = await this.validateAuthentication();
        
        // Handle single file upload
        if (files.length === 1) {
          const mediaItem = await this._uploadSingleMedia(files[0], userId, options);
          return [mediaItem];
        } 
        
        // Handle carousel/multi-file upload
        else if (files.length > 1) {
          return this._createMediaCollection(files, userId, options);
        }
        
        throw createAppError(
          'No files provided',
          ErrorCategory.VALIDATION,
          400,
          'Please select at least one file to upload'
        );
      } catch (error: any) {
        if (error?.name === 'AppError') {
          throw error;
        }
        throw createAppError(
          'Media upload failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to upload media',
          { originalError: error }
        );
      }
    }, { service: 'MediaLibraryService', method: 'uploadMedia' });
  }

  /**
   * Upload a single media file to Supabase storage and record in media_items table
   */
  private async _uploadSingleMedia(
    file: File, 
    userId: string,
    options: {
      folderId?: string;
      tags?: string[];
      altText?: string;
      caption?: string;
    }
  ): Promise<MediaItem> {
    // Determine file type and media type
    const fileType = this._getFileType(file);
    const mediaType = this._getMediaType(file);
    
    // Generate unique file path in storage
    const filePath = `${userId}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
    const bucketPath = mediaType === 'video' ? 'videos' : 'images';
    
    // Upload file to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucketPath)
      .upload(filePath, file);
      
    if (uploadError) {
      throw createAppError(
        'Storage upload failed',
        ErrorCategory.STORAGE,
        undefined,
        'Failed to upload file to storage',
        { originalError: uploadError }
      );
    }

    // Get public URL
    const fileUrl = supabase.storage.from(bucketPath).getPublicUrl(filePath).data.publicUrl;
    
    // If it's a video, generate a thumbnail
    let thumbnailUrl = undefined;
    if (mediaType === 'video') {
      // In a real implementation, this would call a serverless function to generate thumbnail
      // For now, we'll use a placeholder
      thumbnailUrl = 'https://via.placeholder.com/300x200?text=Video+Thumbnail';
    }
    
    // Calculate dimensions and duration for videos
    const metadata: Record<string, any> = {};
    
    // For videos, try to extract duration
    let duration = undefined;
    if (mediaType === 'video') {
      try {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);
        
        // This is async, but for simplicity, we'll use a default value for now
        // In a real app, this would be properly awaited or processed server-side
        video.onloadedmetadata = () => {
          duration = video.duration;
        };
      } catch (e) {
        console.warn('Could not extract video metadata');
      }
    }
    
    // Create media item record in database
    const mediaItem: MediaItem = {
      user_id: userId,
      file_name: file.name,
      file_type: fileType,
      media_type: mediaType,
      file_url: fileUrl,
      file_size: file.size,
      thumbnail_url: thumbnailUrl,
      folder_id: options.folderId,
      tags: options.tags || [],
      alt_text: options.altText,
      caption: options.caption,
      duration,
      metadata
    };
    
    const { data: insertData, error: insertError } = await supabase
      .from('media_items')
      .insert(mediaItem)
      .select()
      .single();
      
    if (insertError) {
      throw createAppError(
        'Database insert failed',
        ErrorCategory.STORAGE,
        undefined,
        'Failed to record media in database',
        { originalError: insertError }
      );
    }
    
    return insertData as MediaItem;
  }

  /**
   * Create a media collection (carousel) with multiple files
   */
  private async _createMediaCollection(
    files: File[],
    userId: string,
    options: {
      collectionName?: string;
      collectionType?: CollectionType;
      platforms?: SocialPlatform[];
      folderId?: string;
      tags?: string[];
      altText?: string;
      caption?: string;
    }
  ): Promise<MediaCollection> {
    // Create collection record
    const collection: MediaCollection = {
      user_id: userId,
      name: options.collectionName || `Collection ${new Date().toLocaleDateString()}`,
      collection_type: options.collectionType || 'carousel',
      platforms: options.platforms,
    };
    
    const { data: collectionData, error: collectionError } = await supabase
      .from('media_collections')
      .insert(collection)
      .select()
      .single();
      
    if (collectionError) {
      throw createAppError(
        'Collection creation failed',
        ErrorCategory.STORAGE,
        undefined,
        'Failed to create media collection',
        { originalError: collectionError }
      );
    }
    
    const collectionId = collectionData.id;
    const uploadedItems: MediaItem[] = [];
    
    // Upload each file and add to collection
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Upload the individual file
        const mediaItem = await this._uploadSingleMedia(file, userId, {
          folderId: options.folderId,
          tags: options.tags,
          altText: options.altText,
          caption: options.caption
        });
        
        // Associate with collection
        await supabase
          .from('media_collection_items')
          .insert({
            collection_id: collectionId,
            media_id: mediaItem.id,
            position: i,
            caption: options.caption
          });
          
        // Update the media item to link to collection
        await supabase
          .from('media_items')
          .update({
            collection_id: collectionId
          })
          .eq('id', mediaItem.id);
          
        uploadedItems.push(mediaItem);
      } catch (error) {
        console.error(`Error uploading file ${i}:`, error);
        // Continue with remaining files
      }
    }
    
    // Fetch the collection with items
    const { data: fullCollection, error: fetchError } = await supabase
      .from('media_collections')
      .select(`
        *,
        items:media_items(*)
      `)
      .eq('id', collectionId)
      .single();
      
    if (fetchError) {
      throw createAppError(
        'Collection fetch failed',
        ErrorCategory.STORAGE,
        undefined,
        'Failed to fetch created collection',
        { originalError: fetchError }
      );
    }
    
    return fullCollection as MediaCollection;
  }
  
  /**
   * Get all media items for the current user
   */
  async getAllMedia(): Promise<MediaItem[]> {
    return withErrorHandling(async () => {
      const userId = await this.validateAuthentication();
      
      const { data: mediaItems, error } = await supabase
        .from('media_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw createAppError(
          'Media fetch failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to fetch media items',
          { originalError: error }
        );
      }
      
      return mediaItems as MediaItem[];
    }, { service: 'MediaLibraryService', method: 'getAllMedia' });
  }
  
  /**
   * Get media items by folder
   */
  async getMediaByFolder(folderId: string): Promise<MediaItem[]> {
    return withErrorHandling(async () => {
      const userId = await this.validateAuthentication();
      
      const { data: mediaItems, error } = await supabase
        .from('media_items')
        .select('*')
        .eq('user_id', userId)
        .eq('folder_id', folderId)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw createAppError(
          'Media fetch failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to fetch media items from folder',
          { originalError: error }
        );
      }
      
      return mediaItems as MediaItem[];
    }, { service: 'MediaLibraryService', method: 'getMediaByFolder' });
  }
  
  /**
   * Get media items by collection
   */
  async getMediaByCollection(collectionId: string): Promise<MediaItem[]> {
    return withErrorHandling(async () => {
      const userId = await this.validateAuthentication();
      
      const { data: mediaItems, error } = await supabase
        .from('media_items')
        .select('*')
        .eq('user_id', userId)
        .eq('collection_id', collectionId)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw createAppError(
          'Media fetch failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to fetch media items from collection',
          { originalError: error }
        );
      }
      
      return mediaItems as MediaItem[];
    }, { service: 'MediaLibraryService', method: 'getMediaByCollection' });
  }
  
  /**
   * Get all folders for the current user
   */
  async getFolders(): Promise<MediaFolder[]> {
    return withErrorHandling(async () => {
      const userId = await this.validateAuthentication();
      
      const { data: folders, error } = await supabase
        .from('media_folders')
        .select('*')
        .eq('user_id', userId)
        .order('name');
        
      if (error) {
        throw createAppError(
          'Folders fetch failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to fetch media folders',
          { originalError: error }
        );
      }
      
      return folders as MediaFolder[];
    }, { service: 'MediaLibraryService', method: 'getFolders' });
  }
  
  /**
   * Get all collections for the current user
   */
  async getCollections(): Promise<MediaCollection[]> {
    return withErrorHandling(async () => {
      const userId = await this.validateAuthentication();
      
      const { data: collections, error } = await supabase
        .from('media_collections')
        .select('*, items:media_items(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw createAppError(
          'Collections fetch failed',
          ErrorCategory.STORAGE,
          undefined,
          'Failed to fetch media collections',
          { originalError: error }
        );
      }
      
      return collections as MediaCollection[];
    }, { service: 'MediaLibraryService', method: 'getCollections' });
  }
  
  /**
   * Get media items with pagination and filtering
   * @deprecated Use getAllMedia, getMediaByFolder, or other specific methods instead
   */
  async _getMediaItems(options: {
    page?: number;
    limit?: number;
    folderId?: string;
    mediaType?: string;
    tags?: string[];
    search?: string;
    collectionId?: string;
  } = {}): Promise<{ items: MediaItem[]; total: number }> {
    return withErrorHandling(async () => {
      try {
        const userId = await this.validateAuthentication();
        
        const page = options.page || 1;
        const limit = options.limit || 20;
        const offset = (page - 1) * limit;
        
        // Start building query
        let query = supabase
          .from('media_items')
          .select('*', { count: 'exact' })
          .eq('user_id', userId);
        
        // Apply filters
        if (options.folderId) {
          query = query.eq('folder_id', options.folderId);
        }
        
        if (options.mediaType) {
          query = query.eq('media_type', options.mediaType);
        }
        
        if (options.collectionId) {
          query = query.eq('collection_id', options.collectionId);
        }
        
        if (options.tags && options.tags.length) {
          // Overlap operator: any of the tags match
          query = query.overlaps('tags', options.tags);
        }
        
        if (options.search) {
          query = query.ilike('file_name', `%${options.search}%`);
        }
        
        // Apply pagination
        query = query.range(offset, offset + limit - 1);
        
        // Order by creation date, newest first
        query = query.order('created_at', { ascending: false });
        
        // Execute query
        const { data, error, count } = await query;
        
        if (error) {
          throw createAppError(
            'Media items fetch failed',
            ErrorCategory.STORAGE,
            undefined,
            'Failed to retrieve media items',
            { originalError: error }
          );
        }
        
        return { 
          items: data || [],
          total: count || 0
        };
      } catch (error: any) {
        if (error?.name === 'AppError') {
          throw error;
        }
        throw createAppError(
          'Media items fetch failed',
          ErrorCategory.UNKNOWN,
          undefined,
          'Failed to retrieve media items',
          { originalError: error }
        );
      }
    }, { service: 'MediaLibraryService', method: 'getMediaItems' });
  }

  /**
   * Create a new folder in the media library
   * @param folderData - Either a string name or a partial MediaFolder object
   * @param parentFolderId - Optional parent folder ID (if folderData is a string)
   * @param description - Optional folder description (if folderData is a string)
   * @param color - Optional folder color (if folderData is a string)
   */
  async createFolder(
    folderData: string | Partial<MediaFolder>,
    parentFolderId?: string, 
    description?: string, 
    color?: string
  ): Promise<MediaFolder> {
    return withErrorHandling(async () => {
      try {
        const userId = await this.validateAuthentication();
        
        // Handle both string name and folder object
        let folder: Partial<MediaFolder>;
        if (typeof folderData === 'string') {
          folder = {
            user_id: userId,
            name: folderData,
            parent_folder_id: parentFolderId,
            description,
            color
          };
        } else {
          folder = {
            ...folderData,
            user_id: userId
          };
        }
        
        const { data, error } = await supabase
          .from('media_folders')
          .insert(folder)
          .select()
          .single();
          
        if (error) {
          throw createAppError(
            'Folder creation failed',
            ErrorCategory.STORAGE,
            undefined,
            'Failed to create media folder',
            { originalError: error }
          );
        }
        
        return data as MediaFolder;
      } catch (error: any) {
        if (error?.name === 'AppError') {
          throw error;
        }
        throw createAppError(
          'Folder creation failed',
          ErrorCategory.UNKNOWN,
          undefined,
          'Failed to create media folder',
          { originalError: error }
        );
      }
    }, { service: 'MediaLibraryService', method: 'createFolder' });
  }

  /**
   * Update an existing media folder
   */
  async updateFolder(folderId: string, updates: Partial<MediaFolder>): Promise<MediaFolder> {
    return withErrorHandling(async () => {
      try {
        const userId = await this.validateAuthentication();
        
        // First check folder ownership
        const { data: existingFolder, error: fetchError } = await supabase
          .from('media_folders')
          .select('*')
          .eq('id', folderId)
          .eq('user_id', userId)
          .single();
          
        if (fetchError) {
          throw createAppError(
            'Folder update failed',
            ErrorCategory.NOT_FOUND,
            404,
            'Folder not found or access denied',
            { originalError: fetchError }
          );
        }
        
        const { data, error } = await supabase
          .from('media_folders')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', folderId)
          .eq('user_id', userId) // Extra security
          .select()
          .single();
          
        if (error) {
          throw createAppError(
            'Folder update failed',
            ErrorCategory.STORAGE,
            undefined,
            'Failed to update media folder',
            { originalError: error }
          );
        }
        
        return data as MediaFolder;
      } catch (error: any) {
        if (error?.name === 'AppError') {
          throw error;
        }
        throw createAppError(
          'Folder update failed',
          ErrorCategory.UNKNOWN,
          undefined,
          'Failed to update media folder',
          { originalError: error }
        );
      }
    }, { service: 'MediaLibraryService', method: 'updateFolder' });
  }
  
  /**
   * Determine file type from File object
   */
  private _getFileType(file: File): MediaFileType {
    const type = file.type.toLowerCase();
    
    if (type.startsWith('image/')) {
      if (type === 'image/gif') {
        return 'gif';
      }
      return 'image';
    } else if (type.startsWith('video/')) {
      return 'video';
    } else if (type.startsWith('audio/')) {
      return 'audio';
    }
    
    // Fallback to checking extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'webp', 'svg'].includes(extension || '')) {
      return 'image';
    } else if (['mp4', 'webm', 'mov', 'avi'].includes(extension || '')) {
      return 'video';
    } else if (['mp3', 'wav', 'ogg'].includes(extension || '')) {
      return 'audio';
    } else if (extension === 'gif') {
      return 'gif';
    }
    
    // Default
    return 'image';
  }
  
  /**
   * Determine higher-level media type from file
   */
  private _getMediaType(file: File): 'image' | 'video' | 'gif' | 'audio' | 'document' {
    const fileType = this._getFileType(file);
    
    switch (fileType) {
      case 'video':
        return 'video';
      case 'gif':
        return 'gif';
      case 'audio':
        return 'audio';
      default:
        return 'image';
    }
  }
}

export default new MediaLibraryService();
